import { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Sparkles, FileText, Check, ExternalLink, AlertCircle, Loader2, RefreshCw } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";

/**
 * NewsLivePreview — split-screen preview pane for the admin News > Generate form.
 *
 * States driven by `preview.status`:
 *   - "generating": LLM call is in flight → skeleton + spinner
 *   - "streaming":  response received, currently being word-revealed
 *   - "done":       fully revealed, shows "View Live" CTA
 *   - "error":      failure state
 */
export function NewsLivePreview({ preview, onDismiss, onRegenerate, busy = false }) {
  // Word-by-word reveal state. When preview.status flips to "streaming", we
  // animate content from "" to preview.content over ~3s.
  const [revealed, setRevealed] = useState("");

  useEffect(() => {
    if (!preview) {
      setRevealed("");
      return;
    }
    if (preview.status !== "streaming") {
      // Final state: show everything immediately
      setRevealed(preview.content || "");
      return;
    }

    // Animate reveal — split on whitespace tokens to keep markdown intact.
    // Honour an absolute duration cap by batching multiple tokens per tick
    // when the article is long.
    const tokens = (preview.content || "").split(/(\s+)/);
    const totalDurationMs = Math.min(4000, Math.max(1500, tokens.length * 12));
    const minIntervalMs = 15;
    const maxTicks = Math.max(1, Math.floor(totalDurationMs / minIntervalMs));
    const tokensPerTick = Math.max(1, Math.ceil(tokens.length / maxTicks));
    const intervalMs = Math.max(minIntervalMs, totalDurationMs / Math.ceil(tokens.length / tokensPerTick));

    let i = 0;
    let buffer = "";
    let cancelled = false;
    const tick = () => {
      if (cancelled) return;
      if (i >= tokens.length) {
        if (typeof preview.onComplete === "function") preview.onComplete();
        return;
      }
      const next = Math.min(i + tokensPerTick, tokens.length);
      buffer += tokens.slice(i, next).join("");
      i = next;
      setRevealed(buffer);
      setTimeout(tick, intervalMs);
    };
    tick();
    return () => { cancelled = true; };
  }, [preview]);

  if (!preview) {
    return (
      <Card className="border-slate-200 border-dashed">
        <CardContent className="p-8 flex flex-col items-center justify-center text-center min-h-[480px] text-slate-400">
          <FileText className="w-12 h-12 mb-3 opacity-50" />
          <h4 className="font-medium text-slate-500 mb-1">Live preview</h4>
          <p className="text-sm">
            Fill in the form and click <span className="font-medium text-navy">Generate Article</span> — your article will appear here as it&apos;s written.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="border-slate-200" data-testid="news-live-preview">
      <CardHeader className="flex flex-row items-center justify-between gap-3 pb-3">
        <CardTitle className="flex items-center gap-2 text-base">
          {preview.status === "generating" && <Loader2 className="w-4 h-4 animate-spin text-gold" />}
          {preview.status === "streaming" && <Sparkles className="w-4 h-4 text-gold animate-pulse" />}
          {preview.status === "done" && <Check className="w-4 h-4 text-emerald-600" />}
          {preview.status === "error" && <AlertCircle className="w-4 h-4 text-red-600" />}
          {preview.status === "generating" && "Generating article…"}
          {preview.status === "streaming" && "Writing your article…"}
          {preview.status === "done" && "Article saved"}
          {preview.status === "error" && "Generation failed"}
        </CardTitle>
        <div className="flex items-center gap-2">
          {(preview.status === "done" || preview.status === "error") && preview.canRegenerate && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={onRegenerate}
              disabled={busy}
              data-testid="news-preview-regenerate"
              title="Re-run the same prompt with a fresh draft (replaces the current one)"
            >
              <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Regenerate
            </Button>
          )}
          {preview.status === "done" && preview.slug && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => window.open(`/news/${preview.slug}`, "_blank", "noopener,noreferrer")}
              data-testid="news-preview-view-live"
            >
              <ExternalLink className="w-3.5 h-3.5 mr-1.5" /> View Live
            </Button>
          )}
          <Button type="button" size="sm" variant="ghost" onClick={onDismiss} data-testid="news-preview-dismiss">
            Dismiss
          </Button>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        {preview.status === "error" ? (
          <div className="rounded-lg bg-red-50 border border-red-200 p-4 text-sm text-red-700">
            {preview.error || "Something went wrong while generating the article."}
          </div>
        ) : (
          <article className="max-h-[600px] overflow-y-auto pr-2">
            {preview.category && (
              <Badge variant="secondary" className="mb-3 bg-navy/5 text-navy">
                {preview.category.replace(/_/g, " ")}
              </Badge>
            )}
            <h1 className="font-heading text-2xl md:text-3xl font-semibold text-navy mb-4 leading-tight">
              {preview.title || "Untitled"}
              {(preview.status === "generating" || preview.status === "streaming") && (
                <span className="ml-1 inline-block w-2 h-6 align-middle bg-gold animate-pulse" />
              )}
            </h1>

            {preview.excerpt && (
              <p className="text-base text-slate-600 mb-6 leading-relaxed font-light italic border-l-2 border-gold pl-4">
                {preview.excerpt}
              </p>
            )}

            <div className="prose prose-sm md:prose-base prose-slate max-w-none
                            prose-headings:font-heading prose-headings:text-navy prose-headings:font-semibold
                            prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg
                            prose-p:text-slate-700 prose-p:leading-relaxed
                            prose-strong:text-navy prose-strong:font-semibold
                            prose-a:text-gold prose-a:no-underline
                            prose-li:text-slate-700 prose-li:my-0.5
                            prose-blockquote:border-l-gold">
              {preview.status === "generating" ? (
                <SkeletonLines />
              ) : (
                <>
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {revealed || ""}
                  </ReactMarkdown>
                  {preview.status === "streaming" && (
                    <span className="inline-block w-2 h-4 align-middle bg-gold animate-pulse ml-0.5" />
                  )}
                </>
              )}
            </div>
          </article>
        )}
      </CardContent>
    </Card>
  );
}

function SkeletonLines() {
  // 5 fake "lines" of varying width — gives a typed-doc feel while we wait.
  const widths = ["w-11/12", "w-10/12", "w-full", "w-9/12", "w-11/12", "w-8/12"];
  return (
    <div className="space-y-3 py-2 animate-pulse">
      {widths.map((w, i) => (
        <div key={i} className={`h-3 rounded bg-slate-200 ${w}`} />
      ))}
    </div>
  );
}

export default NewsLivePreview;
